'use client'

import { InlineNotification } from '@carbon/react'
import type { KmerLocale } from '@kmerhosting/i18n'
import { useEffect, useState } from 'react'

type OfflineCopy = {
  title: string
  message: string
}

const offlineCopy: Record<KmerLocale, OfflineCopy> = {
  en: { title: 'You’re offline', message: 'Some pages and actions may be unavailable until your connection returns.' },
  fr: { title: 'Vous êtes hors ligne', message: 'Certaines pages et actions peuvent être indisponibles jusqu’au retour de votre connexion.' },
  es: { title: 'Estás sin conexión', message: 'Algunas páginas y acciones pueden no estar disponibles hasta que vuelva tu conexión.' },
  pt: { title: 'Está offline', message: 'Algumas páginas e ações podem ficar indisponíveis até a ligação regressar.' },
  de: { title: 'Sie sind offline', message: 'Einige Seiten und Aktionen sind möglicherweise nicht verfügbar, bis die Verbindung wiederhergestellt ist.' },
  'zh-Hans': { title: '您已离线', message: '在网络恢复之前，部分页面和操作可能不可用。' },
  ar: { title: 'أنت غير متصل', message: 'قد لا تتوفر بعض الصفحات والإجراءات حتى يعود الاتصال.' },
  hi: { title: 'आप ऑफ़लाइन हैं', message: 'कनेक्शन वापस आने तक कुछ पेज और कार्रवाइयाँ उपलब्ध नहीं हो सकती हैं।' },
  bn: { title: 'আপনি অফলাইনে আছেন', message: 'সংযোগ ফিরে না আসা পর্যন্ত কিছু পৃষ্ঠা ও কাজ উপলভ্য নাও হতে পারে।' },
  id: { title: 'Anda sedang offline', message: 'Beberapa halaman dan tindakan mungkin tidak tersedia sampai koneksi kembali.' },
  ja: { title: 'オフラインです', message: '接続が戻るまで、一部のページや操作を利用できない場合があります。' },
  ru: { title: 'Нет подключения', message: 'Некоторые страницы и действия могут быть недоступны, пока соединение не восстановится.' },
  it: { title: 'Sei offline', message: 'Alcune pagine e azioni potrebbero non essere disponibili finché la connessione non torna.' },
  ko: { title: '오프라인 상태입니다', message: '연결이 복구될 때까지 일부 페이지와 작업을 사용할 수 없을 수 있습니다.' },
  tr: { title: 'Çevrimdışısınız', message: 'Bağlantınız geri gelene kadar bazı sayfalar ve işlemler kullanılamayabilir.' },
  vi: { title: 'Bạn đang ngoại tuyến', message: 'Một số trang và thao tác có thể không khả dụng cho đến khi kết nối trở lại.' },
  ur: { title: 'آپ آف لائن ہیں', message: 'کنکشن واپس آنے تک کچھ صفحات اور کارروائیاں دستیاب نہیں ہو سکتی ہیں۔' },
  nl: { title: 'U bent offline', message: 'Sommige pagina’s en acties zijn mogelijk niet beschikbaar totdat uw verbinding terug is.' },
  pl: { title: 'Jesteś offline', message: 'Niektóre strony i działania mogą być niedostępne do czasu przywrócenia połączenia.' },
  fa: { title: 'آفلاین هستید', message: 'تا زمان بازگشت اتصال، ممکن است برخی صفحه‌ها و عملیات در دسترس نباشند.' },
}

export function OfflineState({ locale }: { locale: KmerLocale }) {
  const [offline, setOffline] = useState(false)

  useEffect(() => {
    const syncConnectionState = () => setOffline(!navigator.onLine)

    syncConnectionState()
    window.addEventListener('online', syncConnectionState)
    window.addEventListener('offline', syncConnectionState)

    return () => {
      window.removeEventListener('online', syncConnectionState)
      window.removeEventListener('offline', syncConnectionState)
    }
  }, [])

  if (!offline) return null

  const copy = offlineCopy[locale] || offlineCopy.en

  return (
    <div className="site-offline-state" aria-live="polite">
      <InlineNotification
        hideCloseButton
        kind="warning"
        lowContrast
        title={copy.title}
        subtitle={copy.message}
      />
    </div>
  )
}
